/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import { Typeahead } from "react-bootstrap-typeahead";
import {
  Table,
  Button,
  Alert,
  Card,
  CardBody,
  Spinner,
  Row,
  Col,
  Input,
  Label,
} from "reactstrap";
import { formatNumber } from "redux/action/api";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useToasts } from "helper/ToastAdapter";
import { postApi, fetchApi } from "redux/action/api_tbeams";
import { apiURL } from "redux/action/api_tbeams";
import NewRelisePdfWebView from "components/NewRelisePdfWebView";
import { getDistinctObjects } from "./CapitalProject";
import Attachment from "./Attachment";
import { useSelector } from "react-redux";
import moment from "moment";

export default function OtherOverheadExcelUpload({ form, setForm }) {
  const { addToast } = useToasts();
  const user = useSelector((state) => state.auth.user);
  const [uploadedData, setUploadedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [submittedData, setSubmittedData] = useState([]);
  const [distinctAdmins, setDistinctAdminCodes] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const sendToMdaRef = useRef();
  const ccMdaRef = useRef();

  // Fetch MDA list
  const getList = useCallback(() => {
    setFetching(true);
    fetchApi(`get-budget-summary?query_type=recurrent`)
      .then((d) => {
        setFetching(false);
        if (d.result) {
          const distinctAdmins = getDistinctObjects(
            d.result,
            "admin_code",
            "admin_description",
          );
          setDistinctAdminCodes(distinctAdmins);
        }
      })
      .catch((err) => {
        setFetching(false);
        console.log(err);
      });
  }, []);

  useEffect(() => {
    getList();
  }, [getList]);

  // Generate and download sample Excel template
  const downloadSampleExcel = () => {
    const sampleData = [
      {
        mda_code: "1001",
        mda_name: "Ministry of Education",
        economic_code: "2210101",
        economic_name: "Office Supplies",
        amount: 50000,
      },
      {
        mda_code: "1001",
        mda_name: "Ministry of Education",
        economic_code: "2210102",
        economic_name: "Utilities",
        amount: 75000,
      },
      {
        mda_code: "1002",
        mda_name: "Ministry of Health",
        economic_code: "2210101",
        economic_name: "Office Supplies",
        amount: 60000,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Other Overhead");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 12 },
      { wch: 30 },
      { wch: 15 },
      { wch: 30 },
      { wch: 15 },
    ];

    XLSX.writeFile(workbook, "Other_Overhead_Template.xlsx");
    addToast("Sample template downloaded successfully", {
      appearance: "success",
      autoDismiss: true,
      autoDismissTimeout: 3000,
    });
  };

  // Validate Excel data
  const validateExcelData = (data) => {
    const validationErrors = [];
    const requiredColumns = [
      "mda_code",
      "mda_name",
      "economic_code",
      "economic_name",
      "amount",
    ];

    if (data.length === 0) {
      validationErrors.push("Excel file is empty");
      return validationErrors;
    }

    // Check if all required columns exist (case-insensitive)
    const firstRow = data[0];
    const actualColumns = Object.keys(firstRow);

    // Create a map of lowercase column names to actual column names
    const columnMap = {};
    actualColumns.forEach((col) => {
      columnMap[col.toLowerCase().trim()] = col;
    });

    console.log("Actual columns found in Excel:", actualColumns);
    console.log("Column map (lowercase):", Object.keys(columnMap));

    // Check for missing columns (case-insensitive)
    const missingColumns = requiredColumns.filter(
      (col) => !columnMap[col.toLowerCase()],
    );

    if (missingColumns.length > 0) {
      validationErrors.push(
        `Missing required columns: ${missingColumns.join(", ")}. Found columns: ${actualColumns.join(", ")}`,
      );
      return validationErrors;
    }

    // Validate each row
    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because Excel rows start at 1 and we have header

      // Check for empty fields (using actual column names from Excel)
      requiredColumns.forEach((col) => {
        const actualColName = columnMap[col.toLowerCase()];
        if (
          !row[actualColName] ||
          row[actualColName].toString().trim() === ""
        ) {
          validationErrors.push(`Row ${rowNumber}: ${col} is required`);
        }
      });

      // Validate amount is numeric and positive
      const amountColName = columnMap["amount"];
      const amount = parseFloat(row[amountColName]);
      if (isNaN(amount) || amount <= 0) {
        validationErrors.push(
          `Row ${rowNumber}: Amount must be a positive number`,
        );
      }
    });

    return validationErrors;
  };

  // Handle file upload
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Validate data
          const validationErrors = validateExcelData(jsonData);

          if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setUploadedData([]);
            addToast("Excel validation failed. Please check errors.", {
              appearance: "error",
              autoDismiss: true,
              autoDismissTimeout: 5000,
            });
          } else {
            // Create case-insensitive column map for data transformation
            const firstRow = jsonData[0];
            const actualColumns = Object.keys(firstRow);
            const columnMap = {};
            actualColumns.forEach((col) => {
              columnMap[col.toLowerCase().trim()] = col;
            });

            // Transform data to match API format using actual column names
            const transformedData = jsonData.map((row, index) => {
              const mdaCodeCol = columnMap["mda_code"];
              const mdaNameCol = columnMap["mda_name"];
              const economicCodeCol = columnMap["economic_code"];
              const economicNameCol = columnMap["economic_name"];
              const amountCol = columnMap["amount"];

              return {
                id: index + 1,
                mda_code: row[mdaCodeCol].toString().trim(),
                mda_name: row[mdaNameCol].toString().trim(),
                mda_economic_code: row[economicCodeCol].toString().trim(),
                economic_code: row[economicCodeCol].toString().trim(),
                economic_name: row[economicNameCol].toString().trim(),
                mda_description: row[economicNameCol].toString().trim(),
                description: row[economicNameCol].toString().trim(),
                amount: parseFloat(row[amountCol]),
                approved_amount: parseFloat(row[amountCol]),
                projectType: "overhead",
                approval_type: "overhead", // Changed from "Other Overhead" to "overhead"
                ...form,
              };
            });

            setUploadedData(transformedData);
            setErrors([]);
            addToast(
              `Successfully uploaded ${transformedData.length} records`,
              {
                appearance: "success",
                autoDismiss: true,
                autoDismissTimeout: 3000,
              },
            );
          }
        } catch (error) {
          console.error("Error reading Excel file:", error);
          setErrors([
            "Failed to read Excel file. Please ensure it's a valid .xlsx or .xls file.",
          ]);
          addToast("Failed to read Excel file", {
            appearance: "error",
            autoDismiss: true,
            autoDismissTimeout: 5000,
          });
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [form, addToast],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  // Attachment dropzone
  const {
    acceptedFiles: attachmentFiles,
    getRootProps: getAttachmentRootProps,
    getInputProps: getAttachmentInputProps,
    isDragActive: isAttachmentDragActive,
  } = useDropzone({
    accept: "image/*, .pdf, text/plain",
    multiple: true,
  });

  // Sync attachment files
  useEffect(() => {
    setAttachedFiles(attachmentFiles);
  }, [attachmentFiles]);

  // Delete row from uploaded data
  const handleDeleteRow = (id) => {
    setUploadedData((prev) => prev.filter((item) => item.id !== id));
    addToast("Row deleted successfully", {
      appearance: "info",
      autoDismiss: true,
      autoDismissTimeout: 2000,
    });
  };

  // Delete attachment
  const handleDeleteAttachment = (index) => {
    setAttachedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Calculate statistics
  const totalAmount = uploadedData.reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0,
  );
  const uniqueMDAs = new Set(uploadedData.map((item) => item.mda_code)).size;

  // Upload attachments
  const handleApi1 = (approval_no) => {
    if (!attachedFiles || attachedFiles.length === 0) {
      console.log("No files to upload");
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < attachedFiles.length; i++) {
      formData.append("files", attachedFiles[i]);
    }

    const uploadForm = {
      mda_name: form.mda_name || form.mda_description,
      mda_code: form.mda_code,
      mda_economic_code: form.mda_economic_code,
      approved_by: form.approved_by,
      approval: form.approval || "Approved",
      imageId: form.imageId,
      narration: form.narration || "",
      approval_no: approval_no || form.approval_no,
    };

    formData.append("form", JSON.stringify(uploadForm));

    fetch(`${apiURL}/post_images`, {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "success") {
          addToast(
            `${attachedFiles.length} attachment(s) uploaded successfully`,
            {
              appearance: "success",
              autoDismiss: true,
              autoDismissTimeout: 3000,
            },
          );
        }
      })
      .catch((err) => {
        console.error("Upload error:", err);
        addToast("Failed to upload attachments", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 5000,
        });
      });
  };

  // Handle form submission
  const handleSubmit = () => {
    if (uploadedData.length === 0) {
      addToast("Please upload Excel file first", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
      return;
    }

    if (!form.narration || form.narration.trim() === "") {
      addToast("Narration is required", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
      return;
    }

    if (!form.approval_no || form.approval_no.trim() === "") {
      addToast("Reference number is required", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
      return;
    }

    // If Send to MDA not selected, use first MDA from Excel data
    const sendToMda = form.send_to_mda || uploadedData[0]?.mda_code || "";
    const sendToMdaName =
      form.send_to_mda_name || uploadedData[0]?.mda_name || "";

    setIsLoading(true);
    const list = uploadedData.map((item) => ({
      ...item,
      // Ensure economic_code is preserved
      mda_economic_code: item.economic_code || item.mda_economic_code,
      economic_code: item.economic_code || item.mda_economic_code,
      // Form fields
      narration: form.narration,
      approval_no: form.approval_no,
      sent_to_mda: sendToMda,
      send_to_mda_name: sendToMdaName,
      cc: form.cc || "",
      approved_by: form.approved_by || "HE",
      approval_status: "Approval Initiated",
      query_type: "insert",
      // Amount fields
      approved_amount: parseFloat(item.amount) || 0,
      amount: parseFloat(item.amount) || 0,
      // Audit fields
      created_by: user?.username || user?.fullname || "",
      collection_date: moment().format("YYYY-MM-DD"),
      approval_date: moment().format("YYYY-MM-DD"),
    }));

    postApi(`post_approval_collection`, { list, form })
      .then((data) => {
        if (data.success) {
          handleApi1(form.approval_no); // Upload attachments
          const finalData = list.map((i) => ({
            ...i,
            approval_no: form.approval_no,
          }));
          setSubmittedData(finalData);
          addToast("Submit success", {
            appearance: "success",
            autoDismiss: true,
            autoDismissTimeout: 5000,
          });
          setShowForm(false);
        }
      })
      .catch((err) => {
        console.error(err);
        addToast(err.message || "Submission failed", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 5000,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!showForm) {
    return (
      <NewRelisePdfWebView
        selectedData={submittedData}
        selecOffer={formatNumber}
      />
    );
  }

  return (
    <div className="px-4 py-3">
      {/* Header Section */}
      <Row className="mb-4">
        <Col md={12}>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #4caf50",
            }}
          >
            <MDTypography
              variant="h6"
              style={{ marginBottom: "10px", color: "#2c3e50" }}
            >
              Other Overhead - Excel Upload
            </MDTypography>
            <MDTypography variant="body2" style={{ color: "#6c757d" }}>
              Fill in the required information below, then upload Excel file
            </MDTypography>
          </div>
        </Col>
      </Row>

      {/* FORM FIELDS FIRST - BEFORE EXCEL UPLOAD */}
      <Row className="mb-4">
        <Col md={12}>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #4caf50",
              marginBottom: "15px",
            }}
          >
            <MDTypography
              variant="h6"
              style={{ marginBottom: "10px", color: "#2c3e50" }}
            >
              Release Information
            </MDTypography>
          </div>
        </Col>
        <Col md={6}>
          <Label style={{ fontWeight: "600", color: "#495057" }}>
            Narration <span style={{ color: "red" }}>*</span>
          </Label>
          <Input
            type="textarea"
            value={form.narration}
            name="narration"
            onChange={(e) =>
              setForm((p) => ({ ...p, narration: e.target.value }))
            }
            rows={4}
            style={{
              borderRadius: "6px",
              border: "1px solid #ced4da",
              padding: "10px",
            }}
            placeholder="Enter narration for this release..."
          />
        </Col>
        <Col md={6}>
          <Row>
            <Col md={12} className="mb-3">
              <Label style={{ fontWeight: "600", color: "#495057" }}>
                Reference Number <span style={{ color: "red" }}>*</span>
              </Label>
              <Input
                value={form.approval_no}
                name="approval_no"
                onChange={(e) =>
                  setForm((p) => ({ ...p, approval_no: e.target.value }))
                }
                style={{
                  borderRadius: "6px",
                  border: "1px solid #ced4da",
                  padding: "8px 12px",
                }}
                placeholder="Enter reference number"
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label style={{ fontWeight: "600", color: "#495057" }}>
                Send to MDA{" "}
                <span style={{ color: "#6c757d", fontSize: "12px" }}>
                  (Optional - defaults to first MDA in Excel)
                </span>
              </Label>
              <Typeahead
                size="sm"
                id="send-to-mda-typeahead"
                ref={sendToMdaRef}
                onChange={(selected) => {
                  if (selected?.length) {
                    const selectedItem = selected[0];
                    setForm((p) => ({
                      ...p,
                      send_to_mda: selectedItem.admin_code,
                      send_to_mda_name: selectedItem.admin_description,
                    }));
                  } else {
                    setForm((p) => ({
                      ...p,
                      send_to_mda: "",
                      send_to_mda_name: "",
                    }));
                  }
                }}
                options={distinctAdmins.length ? distinctAdmins : []}
                labelKey={(option) =>
                  `${option.admin_code} - ${option.admin_description}`
                }
                placeholder="Select MDA to send to (optional)"
                disabled={fetching}
              />
              {fetching && <Spinner size="sm" color="success" />}
            </Col>
            <Col md={12}>
              <Label style={{ fontWeight: "600", color: "#495057" }}>
                Copy to (CC)
              </Label>
              <Typeahead
                size="sm"
                id="cc-mda-typeahead"
                ref={ccMdaRef}
                onChange={(selected) => {
                  if (selected?.length) {
                    const selectedItem = selected[0];
                    setForm((p) => ({
                      ...p,
                      cc: selectedItem.admin_description,
                      cc_mda_name: selectedItem.admin_description,
                    }));
                  } else {
                    setForm((p) => ({
                      ...p,
                      cc: "",
                      cc_mda_name: "",
                    }));
                  }
                }}
                options={distinctAdmins.length ? distinctAdmins : []}
                labelKey={(option) => `${option.admin_description}`}
                placeholder="Select MDA for CC (optional)"
                disabled={fetching}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Attachments Section */}
      <Row className="mb-4">
        <Col md={12}>
          <div
            style={{
              backgroundColor: "#f3e5f5",
              padding: "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #9c27b0",
              marginBottom: "15px",
            }}
          >
            <MDTypography
              variant="h6"
              style={{ marginBottom: "10px", color: "#6a1b9a" }}
            >
              Attachments (Optional)
            </MDTypography>
          </div>
        </Col>
        <Col md={12}>
          <Attachment
            acceptedFiles={attachedFiles}
            getRootProps={getAttachmentRootProps}
            getInputProps={getAttachmentInputProps}
            isDragActive={isAttachmentDragActive}
            onDeleteFile={handleDeleteAttachment}
          />
        </Col>
      </Row>

      {/* Download Sample Button */}
      <Row className="mb-3">
        <Col md={12}>
          <Button
            color="info"
            onClick={downloadSampleExcel}
            style={{
              borderRadius: "6px",
              padding: "10px 20px",
              fontWeight: "600",
            }}
          >
            <i className="fas fa-download" style={{ marginRight: "8px" }}></i>
            Download Sample Excel Template
          </Button>
        </Col>
      </Row>

      {/* Upload Dropzone */}
      <Row className="mb-4">
        <Col md={12}>
          <div
            {...getRootProps()}
            style={{
              border: "2px dashed #4caf50",
              borderRadius: "8px",
              padding: "40px",
              textAlign: "center",
              backgroundColor: isDragActive ? "#e8f5e9" : "#f9f9f9",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            <input {...getInputProps()} />
            <i
              className="fas fa-cloud-upload-alt"
              style={{
                fontSize: "48px",
                color: "#4caf50",
                marginBottom: "15px",
              }}
            ></i>
            {isDragActive ? (
              <p style={{ fontSize: "16px", color: "#2c3e50", margin: 0 }}>
                Drop the Excel file here...
              </p>
            ) : (
              <>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#2c3e50",
                    marginBottom: "5px",
                  }}
                >
                  Drag and drop an Excel file here, or click to select
                </p>
                <p style={{ fontSize: "14px", color: "#6c757d", margin: 0 }}>
                  Supported formats: .xlsx, .xls
                </p>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* Validation Errors */}
      {errors.length > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Alert color="danger" style={{ borderRadius: "8px" }}>
              <h5 style={{ marginBottom: "10px" }}>
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ marginRight: "8px" }}
                ></i>
                Validation Errors ({errors.length})
              </h5>
              <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
                {errors.slice(0, 10).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
                {errors.length > 10 && (
                  <li>... and {errors.length - 10} more errors</li>
                )}
              </ul>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Statistics Card */}
      {uploadedData.length > 0 && (
        <>
          <Row className="mb-4">
            <Col md={12}>
              <Card
                style={{
                  borderRadius: "8px",
                  border: "1px solid #4caf50",
                  boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
                }}
              >
                <CardBody>
                  <MDTypography
                    variant="h6"
                    style={{ marginBottom: "15px", color: "#2c3e50" }}
                  >
                    <i
                      className="fas fa-chart-bar"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Upload Summary
                  </MDTypography>
                  <Row>
                    <Col md={3}>
                      <div style={{ textAlign: "center", padding: "15px" }}>
                        <MDTypography
                          variant="h4"
                          style={{ color: "#4caf50", fontWeight: "bold" }}
                        >
                          {uploadedData.length}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          style={{ color: "#6c757d" }}
                        >
                          Total Records
                        </MDTypography>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div style={{ textAlign: "center", padding: "15px" }}>
                        <MDTypography
                          variant="h4"
                          style={{ color: "#2196f3", fontWeight: "bold" }}
                        >
                          {uniqueMDAs}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          style={{ color: "#6c757d" }}
                        >
                          Unique MDAs
                        </MDTypography>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ textAlign: "center", padding: "15px" }}>
                        <MDTypography
                          variant="h4"
                          style={{ color: "#ff9800", fontWeight: "bold" }}
                        >
                          ₦ {formatNumber(totalAmount)}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          style={{ color: "#6c757d" }}
                        >
                          Total Amount
                        </MDTypography>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Preview Table */}
          <Row className="mb-4">
            <Col md={12}>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "15px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #2196f3",
                  marginBottom: "15px",
                }}
              >
                <MDTypography
                  variant="h6"
                  style={{ marginBottom: "10px", color: "#2c3e50" }}
                >
                  <i
                    className="fas fa-table"
                    style={{ marginRight: "8px" }}
                  ></i>
                  Preview Data ({uploadedData.length} records)
                </MDTypography>
              </div>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <Table
                  bordered
                  striped
                  size="sm"
                  style={{ fontSize: "12px", marginBottom: 0 }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "10%" }}>MDA Code</th>
                      <th style={{ width: "25%" }}>MDA Name</th>
                      <th style={{ width: "12%" }}>Economic Code</th>
                      <th style={{ width: "28%" }}>Economic Name</th>
                      <th style={{ width: "15%" }}>Amount</th>
                      <th style={{ width: "5%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedData.map((row, index) => (
                      <tr key={row.id}>
                        <td>{index + 1}</td>
                        <td>{row.mda_code}</td>
                        <td>{row.mda_name}</td>
                        <td>{row.economic_code}</td>
                        <td>{row.economic_name}</td>
                        <td style={{ textAlign: "right", fontWeight: "600" }}>
                          ₦ {formatNumber(row.amount)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeleteRow(row.id)}
                            style={{ padding: "2px 8px" }}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr
                      style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}
                    >
                      <td colSpan="5" style={{ textAlign: "right" }}>
                        Total Amount:
                      </td>
                      <td style={{ textAlign: "right", color: "#4caf50" }}>
                        ₦ {formatNumber(totalAmount)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Col>
          </Row>

          {/* Submit Button */}
          <Row>
            <Col md={12}>
              <center>
                <Button
                  color="success"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  size="lg"
                  style={{
                    borderRadius: "8px",
                    padding: "12px 50px",
                    fontWeight: "600",
                    boxShadow: "0 4px 6px rgba(76, 175, 80, 0.3)",
                  }}
                >
                  {isLoading ? (
                    <Spinner color="light" size="sm" />
                  ) : (
                    <>
                      <i
                        className="fas fa-paper-plane"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Submit Release ({uploadedData.length} records)
                    </>
                  )}
                </Button>
              </center>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
